/** @fileoverview Searches, pages, and opens saved Fabrik conversations. */

import { useMemo, useState } from 'react';
import {
  ArrowLeft,
  CheckCircle2,
  CircleHelp,
  LoaderCircle,
  MessageSquareText,
  Search,
} from 'lucide-react';
import { Button } from '../ui/button';
import LoadingState from '../ui/loading-state';
import { ScrollArea, ScrollAreaContent, ScrollAreaViewport, ScrollBar } from '../ui/scroll-area';
import type { ChatHistoryProps } from './types';

const formatConversationDate = (value?: string) => {
  const timestamp = value ? Date.parse(value) : Number.NaN;
  return Number.isNaN(timestamp)
    ? ''
    : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(timestamp);
};

export default function ChatHistory({
  conversations,
  conversationActivities,
  loading,
  total,
  loadFailed,
  onRetry,
  onLoadMore,
  onOpenConversation,
  onBackToAssistant,
}: ChatHistoryProps) {
  const [search, setSearch] = useState('');
  const filteredConversations = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return query
      ? conversations.filter((conversation) =>
          conversation.title.toLocaleLowerCase().includes(query)
        )
      : conversations;
  }, [conversations, search]);
  const retry = () => {
    setSearch('');
    onRetry();
  };

  return (
    // Centered column so the list stays readable when the chat is full width; no effect in the sidebar.
    <div className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col @5xl:pt-4">
      <div className="flex shrink-0 items-center gap-2 px-4 pt-2">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-app-text-muted hover:bg-app-surface-muted-strong hover:text-app-text"
          aria-label="Back to assistant"
          title="Back to assistant"
          onClick={onBackToAssistant}
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        </Button>
        <h2 className="text-sm font-semibold text-app-text">Recent conversations</h2>
      </div>
      <label className="relative mx-4 mt-2 mb-3 block shrink-0">
        <Search
          className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-app-text-faint"
          aria-hidden="true"
        />
        <input
          type="search"
          aria-label="Search conversations"
          placeholder="Search conversations"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="h-10 w-full rounded-md border border-app-border bg-app-surface-card pl-9 pr-3 text-sm text-app-text outline-none placeholder:text-app-text-faint focus:border-app-brand-strong"
        />
      </label>
      <ScrollArea className="min-h-0 w-full min-w-0 max-w-full flex-1">
        <ScrollAreaViewport className="max-w-full overflow-x-hidden overscroll-contain">
          <ScrollAreaContent className="w-full min-w-0! max-w-full">
            {loadFailed ? (
              <div className="flex flex-col items-center gap-2 px-5 py-8 text-center text-sm text-app-text-muted">
                <p>Could not load conversations.</p>
                <Button type="button" variant="ghost" onClick={retry}>
                  Try again
                </Button>
              </div>
            ) : loading && !conversations.length ? (
              <LoadingState className="px-5 py-8">Loading conversations…</LoadingState>
            ) : filteredConversations.length ? (
              <div className="divide-y divide-app-border">
                {filteredConversations.map((conversation) => {
                  const date = formatConversationDate(conversation.updated_at);
                  const activity = conversationActivities.get(conversation.id);
                  return (
                    <button
                      className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-app-surface-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-app-brand-strong"
                      key={conversation.id}
                      type="button"
                      onClick={() => onOpenConversation(conversation)}
                    >
                      <MessageSquareText
                        className="h-4 w-4 shrink-0 text-app-brand-strong"
                        aria-hidden="true"
                      />
                      <span className="min-w-0 flex-1 truncate text-sm text-app-text">
                        {conversation.title || 'Untitled conversation'}
                      </span>
                      {activity && (
                        <span
                          role="status"
                          aria-label={
                            activity === 'streaming'
                              ? 'Assistant is working'
                              : activity === 'waiting-for-input'
                              ? 'Waiting for your answer'
                              : 'Assistant finished — not opened'
                          }
                          title={
                            activity === 'streaming'
                              ? 'Assistant is working'
                              : activity === 'waiting-for-input'
                              ? 'Waiting for your answer'
                              : 'Assistant finished — not opened'
                          }
                          className={`shrink-0 ${
                            activity === 'waiting-for-input'
                              ? 'text-app-warning'
                              : 'text-app-brand-strong'
                          }`}
                        >
                          {activity === 'streaming' ? (
                            <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
                          ) : activity === 'waiting-for-input' ? (
                            <CircleHelp className="h-4 w-4" aria-hidden="true" />
                          ) : (
                            <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                          )}
                        </span>
                      )}
                      {date && (
                        <time
                          className="shrink-0 text-xs text-app-text-subtle"
                          dateTime={conversation.updated_at}
                        >
                          {date}
                        </time>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="px-5 py-8 text-center text-sm text-app-text-muted">
                {search.trim()
                  ? `No conversations match “${search.trim()}”.`
                  : 'No conversations yet.'}
              </p>
            )}
            {!search.trim() &&
              conversations.length > 0 &&
              conversations.length < total &&
              (loading ? (
                <LoadingState className="p-3">Loading conversations…</LoadingState>
              ) : (
                <div className="flex justify-center p-3">
                  <Button type="button" variant="ghost" onClick={onLoadMore}>
                    Load more
                  </Button>
                </div>
              ))}
          </ScrollAreaContent>
        </ScrollAreaViewport>
        <ScrollBar />
      </ScrollArea>
    </div>
  );
}
